/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { forwardRef, useEffect, useState } from "react";
import { useFormik } from "formik";
import { Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, DialogTrigger, Field, Input, Text, makeStyles, tokens } from "@fluentui/react-components";
import { ArrowCircleLeftFilled, ArrowCircleLeftRegular, ArrowCircleRightFilled, ArrowCircleRightRegular, bundleIcon, CalendarAddRegular } from "@fluentui/react-icons";
import { DatePicker } from "@fluentui/react-datepicker-compat";
import { Column, Row } from "../common";
import { useAccountState } from "../../redux/reducers/accountReducer";
import { useUtilities } from "../../utilities/useUtilities";
import { useCommonStyles } from "../common/Styles/commonStyles";
import { TimeTrackingEntry, Task, useInitialValues, UserAccount } from "../../data/types";
import { useValidation } from "../../data/types/validation";
import { useDialogControls } from "./useDialogControls";

const useStyles = makeStyles({
    field: {
        width: '100%',
        fontWeight: tokens.fontWeightBold
    },
    descrip: {
        height: '4rem',
    },
    hoursInput: {
        width: '8rem',
        height: '4rem',
        fontSize: '42px'
    },
    datePicker: {
        visibility: 'collapse',
    },
});
export type AddTimeEntryForTaskDialogProps = {
    caseId?: string;
    onSubmit?: (timeEntry: TimeTrackingEntry) => Promise<void>;
    children?: React.ReactElement;
    task?: Task;
    open?: boolean;
    onOpenChange?: (isOpen: boolean) => void;
}
export const AddTimeEntryForTaskDialog = forwardRef((props: AddTimeEntryForTaskDialogProps, _ref) => {

    const { caseId, children, onSubmit, task, open, onOpenChange } = props;

    //Hooks
    const { primaryAccount } = useAccountState();
    const { user } = primaryAccount as UserAccount ?? {};
    const initValues = useInitialValues();
    const utils = useUtilities();
    const { validateTimeTrackEntry } = useValidation();

    //State
    const [dateOpen, setDateOpen] = useState<boolean>(false);

    //Styles
    const commonStyles = useCommonStyles();
    const styles = useStyles();

    //Form Stuff
    const initTimeEntry = (): TimeTrackingEntry => {
        return { ...initValues.timeTrackEntry(caseId as string, user.userId), taskId: task?.taskId, isBillable: task?.billable, description: task?.title }
    }
    const timeEntryForm = useFormik({
        initialValues: initTimeEntry(),
        onSubmit: async (values, { resetForm }) => {
            const timeEntry = values as TimeTrackingEntry;

            if (isNaN(timeEntry.hours)) {
                timeEntry.hours = 0;
            }

            if (!!timeEntry.hours && !!timeEntry.description) {
                !!onSubmit && (await onSubmit(timeEntry));
                resetForm({ values: initTimeEntry() });
            }
        },
        validationSchema: validateTimeTrackEntry.schema,
    });

    //Dialog Controls
    const [dialogOpen, onOpenChangeHandler] = useDialogControls(open, onOpenChange);

    //UI Events
    const clickSubmit = (e: any) => {
        e.stopPropagation();
        timeEntryForm.submitForm();
    }
    const clickCancel = (e: any) => {
        e.stopPropagation();
        timeEntryForm.resetForm({ values: initTimeEntry() });
    }
    const onHoursChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
        const hours = e.target.value;
        timeEntryForm.setFieldValue('hours', Number(hours).toFixed(2));
    }
    const setEntryDate = (date: Date) => {
        timeEntryForm.setFieldValue('date', date);
    }
    const clickLeft = (e: any) => {
        e.stopPropagation();

        const newDate = new Date(timeEntryForm.values.date as Date);
        newDate.setDate(newDate.getDate() - 1);
        setEntryDate(newDate);
    }
    const clickRight = (e: any) => {
        e.stopPropagation();

        const newDate = new Date(timeEntryForm.values.date as Date);
        newDate.setDate(newDate.getDate() + 1);
        setEntryDate(newDate);
    }

    //Effects
    useEffect(() => {
        if (dialogOpen && caseId && task) {
            timeEntryForm.resetForm({ values: initTimeEntry() });
        }
    }, [dialogOpen, caseId, task]);

    const ArrowLeftBundle = bundleIcon(ArrowCircleLeftFilled, ArrowCircleLeftRegular);
    const ArrowRightBundle = bundleIcon(ArrowCircleRightFilled, ArrowCircleRightRegular);

    return (
        <Dialog
            open={dialogOpen}
            onOpenChange={onOpenChangeHandler}
        >
            <DialogTrigger disableButtonEnhancement>{children}</DialogTrigger>
            <DialogSurface aria-describedby={undefined} mountNode={undefined} style={{ maxWidth: '650px' }}>
                <DialogBody>
                    <DialogTitle>
                        <Row gap='medium'>
                            <Column width='auto' vAlign='middle'>
                                <CalendarAddRegular />
                            </Column>
                            <Column width='auto' vAlign='middle'>
                                Add {task?.billable ? 'Billable' : 'Non-Billable'} Time
                            </Column>
                        </Row>
                    </DialogTitle>
                    <DialogContent>
                        <Column noGap>
                            <Row noWrap gap='small' width='auto'>
                                <Column width='auto' vAlign='middle' padding='pr-3'>
                                    <span className={styles.field}>Date:</span>
                                </Column>
                                <Column width='auto' align='right' vAlign='middle'>
                                    <Button
                                        className={commonStyles.button.root}
                                        appearance='subtle'
                                        shape='rounded'
                                        size='small'
                                        onClick={clickLeft}
                                        icon={<ArrowLeftBundle color={tokens.colorBrandStroke1} />}
                                        data-testid='add-timeentry-fortask-left-button'
                                    />
                                </Column>
                                <Column width='auto' align='center' vAlign='middle'>
                                    <Button
                                        appearance='subtle'
                                        size='large'
                                        onClick={() => setDateOpen(true)}
                                        data-testid='add-timeentry-fortask-date-button'
                                    >
                                        {utils.date.formatDate(
                                            new Date(timeEntryForm.values.date || 0),
                                            { month: 'long', day: 'numeric', year: 'numeric' },
                                            true,
                                        )}
                                    </Button>
                                </Column>
                                <Column width='auto' align='left' vAlign='middle'>
                                    <Button
                                        className={commonStyles.button.root}
                                        appearance='subtle'
                                        shape='rounded'
                                        size='small'
                                        onClick={clickRight}
                                        icon={<ArrowRightBundle color={tokens.colorBrandStroke1} />}
                                        data-testid='add-timeentry-fortask-right-button'
                                    />
                                </Column>
                            </Row>
                            <Column height='0'>
                                <DatePicker
                                    className={styles.datePicker}
                                    open={dateOpen}
                                    value={timeEntryForm.values.date}
                                    onOpenChange={(open) => setDateOpen(open)}
                                    onSelectDate={(date) => {
                                        setEntryDate(date as Date);
                                        setDateOpen(false);
                                    }}
                                    placeholder='Select a date...'
                                    data-testid='add-timeentry-fortask-datepicker'
                                />
                            </Column>
                            <Row padding='pb-3'>
                                <Column scroll>
                                    <Field className={styles.field} label='Task'>
                                        <Text className={styles.descrip}>{timeEntryForm.values.description}</Text>
                                    </Field>
                                </Column>
                                <Column width='auto'>
                                    <Field className={styles.field} label='Hours' required validationMessage={timeEntryForm.errors.hours}>
                                        <Input
                                            className={styles.hoursInput}
                                            name='hours'
                                            value={`${timeEntryForm.values.hours || ''}`}
                                            onChange={timeEntryForm.handleChange}
                                            onBlur={onHoursChanged}
                                            placeholder='0.00'
                                            data-testid='add-timeentry-fortask-hours-input'
                                        />
                                    </Field>
                                </Column>
                            </Row>
                        </Column>
                    </DialogContent>
                    <DialogActions>
                        <DialogTrigger disableButtonEnhancement>
                            <Button
                                type='submit'
                                appearance='primary'
                                onClick={clickSubmit}
                                disabled={!timeEntryForm.dirty || !timeEntryForm.isValid}
                                data-testid='add-timeentry-fortask-submit-button'
                            >
                                Submit Time
                            </Button>
                        </DialogTrigger>
                        <DialogTrigger disableButtonEnhancement>
                            <Button
                                appearance='secondary'
                                onClick={clickCancel}
                                data-testid='add-timeentry-fortask-cancel-button'
                            >
                                Cancel
                            </Button>
                        </DialogTrigger>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
});